import {readOpinionIntent, continueOpinion, opinionIntentKey} from './opinionIntent.js';

const keys = {poll: 'wiki-chat-poll-intent', name: 'wiki-chat-name-intent', opinion: opinionIntentKey};
const panes = new Set(['article', 'names', 'discussion']);
const discussionTabs = new Set(['chat', 'threads']);

// An explicit phone destination wins over open-panel links and saved drafts.
function readDestination(url) {
  const thread = url.searchParams.get('thread');
  const entry = thread ? 'threads' : url.searchParams.get('conversation');
  const issue = url.searchParams.get('issue');
  const explicitPane = panes.has(url.searchParams.get('pane')) ? url.searchParams.get('pane') : null;
  return {termId: url.searchParams.get('term') || null, thread,
    entry: discussionTabs.has(entry) ? entry : explicitPane === 'discussion' ? 'chat' : null,
    // `all` is distinct from a bare legacy article URL and from synthetic names.
    hasIssue: Boolean(issue), scope: issue === 'all' ? null : issue || 'names', explicitPane,
    pane: explicitPane || (issue ? 'names' : discussionTabs.has(entry) ? 'discussion' : 'article')};
}

function writeDestinationPane(url, pane, context) {
  url.searchParams.set('pane', pane);
  if (pane === 'names' && !url.searchParams.has('issue'))
    url.searchParams.set('issue', context.currentPollScope === null ? 'all' : context.currentPollScope || 'names');
  if (pane === 'discussion' && !url.searchParams.has('thread') && !discussionTabs.has(url.searchParams.get('conversation')))
    url.searchParams.set('conversation', discussionTabs.has(context.conversationTab) ? context.conversationTab : 'chat');
}

export function readLoginIntent(storage, kind, now = Date.now()) {
  if (kind === 'opinion') return readOpinionIntent(storage);
  try {
    const value = JSON.parse(storage.getItem(keys[kind]));
    return value && typeof value.termId === 'string' && Number.isFinite(value.createdAt) &&
      value.createdAt <= now && now - value.createdAt < 1800000 ? value : null;
  } catch { return null; }
}

// The interface expresses destinations. Ordering of URL, draft restoration,
// panel visibility, scroll and focus is private to this module.
export function createArticleWorkspace({context, update, storage, readURL, writeURL, focus, scroll, readDrafts}) {
  let positions = {article: 0, names: 0}, scopes = new Map(), termId = null;
  const clearIntent = kind => {
    update({['pending' + kind[0].toUpperCase() + kind.slice(1)]: null});
    try { storage.removeItem(keys[kind]); } catch {}
  };
  const cleanArticleURL = url => {
    for (const key of ['thread', 'conversation', 'issue', 'pane', 'view', 'oldid', 'diff']) url.searchParams.delete(key);
  };
  function applyPane(pane) {
    const c = context();
    // Open state belongs to each panel; phone destination is a separate choice.
    update({mobilePane: pane});
    if (pane === 'names') update({namesOpen: true});
    else if (pane === 'discussion') update({discussionOpen: true});
    else if (c.edit) focus('editor');
    update({selection: null});
  }
  function pane(paneName) {
    const url = readURL();
    writeDestinationPane(url, paneName, context());
    if (url.href !== readURL().href) writeURL(url, 'push');
    applyPane(paneName);
  }
  function conversation(comment, tab = comment ? 'threads' : 'chat', composer = null, syncURL = true) {
    const url = readURL();
    if (comment) url.searchParams.set('thread', comment.id); else url.searchParams.delete('thread');
    if (tab === 'chat' || tab === 'threads') url.searchParams.set('conversation', tab); else url.searchParams.delete('conversation');
    writeDestinationPane(url, 'discussion', {...context(), conversationTab: tab});
    if (syncURL && url.href !== readURL().href) writeURL(url, 'push');
    update({conversationTab: tab, active: comment?.id || null, composer, selection: null});
    applyPane('discussion');
    if (composer) focus('composer');
  }
  return {
    clearIntent,
    requireLogin(kind, value = {}) {
      const intent = {...value, termId: context().term.id, createdAt: Date.now()};
      update({['pending' + kind[0].toUpperCase() + kind.slice(1)]: intent, modal: 'login'});
      try { storage.setItem(keys[kind], JSON.stringify(intent)); } catch {}
    },
    navigate(destination) {
      const c = context(), url = readURL();
      if (destination.type === 'term') {
        if (c.pendingOpinion && c.pendingOpinion.termId !== destination.term.id) clearIntent('opinion');
        cleanArticleURL(url); url.searchParams.set('term', destination.term.id);
        if (destination.conversation) {
          url.searchParams.set('conversation', destination.conversation);
          writeDestinationPane(url, 'discussion', c);
        }
        update({aboutOpen: false, adminOpen: false, historyOpen: false, selectedId: destination.term.id});
        if (destination.conversation) conversation(null, destination.conversation, null, false);
      } else {
        clearIntent('opinion');
        for (const key of ['term', 'thread', 'conversation', 'issue', 'pane', 'view', 'oldid', 'diff', 'q']) url.searchParams.delete(key);
        url.hash = '';
        if (destination.type !== 'home') url.searchParams.set('view', destination.type);
        update({selectedId: null, aboutOpen: destination.type === 'about', adminOpen: destination.type === 'admin',
          historyOpen: false, discussionOpen: false, namesOpen: false, mobilePane: 'article', selection: null});
      }
      writeURL(url, 'push');
      if (c.screenWidth <= 850 || destination.type === 'about') scroll('window', 0);
    },
    restoreURL() {
      const url = readURL(), destination = readDestination(url), c = context();
      const {termId, thread, entry, hasIssue, scope, explicitPane} = destination;
      update({aboutOpen: url.searchParams.get('view') === 'about', adminOpen: url.searchParams.get('view') === 'admin',
        selectedId: termId, historyOpen: url.searchParams.get('view') === 'history',
        discussionOpen: Boolean(entry), mobilePane: destination.pane,
        namesOpen: hasIssue || explicitPane === 'names' || (termId === c.selectedId && c.namesOpen),
        pollScope: {termId, id: scope},
        ...(entry ? {active: thread || null, conversationTab: entry, composer: null} : {})});
    },
    restoreConversation() {
      const c = context(), destination = readDestination(readURL());
      const {thread, entry, hasIssue, scope, explicitPane} = destination;
      const saved = c.user?.id && c.selectedId ? readDrafts(c.user.id, c.selectedId)[0] : null;
      update({selection: null, active: entry ? thread || null : saved?.composer?.parentId || null,
        composer: entry ? null : saved?.composer || null, discussionOpen: Boolean(entry || saved),
        conversationTab: entry || 'chat', pollScope: {termId: c.selectedId, id: scope},
        ...(hasIssue || explicitPane === 'names' ? {namesOpen: true} : {}),
        mobilePane: explicitPane || (hasIssue ? 'names' : entry || saved ? 'discussion' : 'article')});
      scroll('article', 0);
    },
    conversation,
    compose(next) { conversation(null, 'threads', next); },
    pane,
    close(paneName) {
      const c = context();
      if (c.mobilePane === paneName) {
        const url = readURL();
        writeDestinationPane(url, 'article', c);
        if (url.href !== readURL().href) writeURL(url, 'push');
      }
      update({...(paneName === 'names' ? {namesOpen: false} : {discussionOpen: false}),
        ...(c.mobilePane === paneName ? {mobilePane: 'article'} : {})});
      focus(c.screenWidth <= 850 ? 'article-tab' : paneName === 'names' ? 'poll-return' : c.conversationTab === 'ai' && c.edit ? 'ai-toggle' : 'discussion-toggle');
    },
    toggle(paneName) {
      const c = context();
      if (paneName === 'names' ? c.namesVisible : c.discussionOpen) this.close(paneName);
      else {
        pane(paneName);
        focus(paneName === 'names' ? c.currentPollScope === 'names' ? 'poll-heading' : 'poll-content' : 'discussion-tab');
      }
    },
    pollScope(id) {
      const c = context();
      if (c.namesVisible) scopes.set(c.term.id + ':' + c.currentPollScope, c.namesScroll());
      update({pollScope: {termId: c.term.id, id}});
      const url = readURL();
      url.searchParams.set('issue', id === null ? 'all' : id);
      writeDestinationPane(url, 'names', c);
      if(url.href!==readURL().href)writeURL(url,'push');
    },
    restoreScope() {
      const c = context(), top = scopes.get(c.term?.id + ':' + c.currentPollScope) || 0;
      positions.names = top; scroll('names', top);
    },
    saveScroll(paneName, top) { positions[paneName] = top; },
    restorePanes() {
      const c = context();
      if (c.articleVisible) scroll(c.screenWidth <= 850 ? 'window' : 'article', positions.article);
      if (c.namesVisible) scroll('names', positions.names);
    },
    resetTerm() {
      const id = context().term?.id;
      if (id === termId) return;
      termId = id; positions = {article: 0, names: 0}; scopes = new Map(); scroll('names', 0);
    },
    resume() {
      const c = context();
      if (!c.user || c.modal === 'login' || c.edit || c.historyOpen) return;
      if (c.pendingOpinion?.termId === c.term?.id && !(c.pendingOpinion.anchor.field === 'english' && c.englishLoading)) {
        const next = continueOpinion(c.pendingOpinion, c.term, c.english);
        if (next) { clearIntent('opinion'); this.compose(next); focus('clear-selection'); if (next.notice) c.tell(next.notice); }
      }
      if (c.pendingName?.termId === c.term?.id) {
        this.pollScope('names'); pane('names'); update({nameProposeRequest: c.nameProposeRequest + 1});
        clearIntent('name'); focus('name-propose');
      }
      if (c.pendingPoll?.termId === c.term?.id) {
        this.pollScope(c.pendingPoll.scope); pane('names'); update({pollResume: c.pollResume + 1}); clearIntent('poll');
      }
    }
  };
}
